/* tslint:disable */
/* eslint-disable */
/**
 * CyberWell Aegis API
 * Multi-tenant case management API with JWT auth, RBAC (Casbin), audit logging, and tenant isolation.
 *
 * The version of the OpenAPI document: v1
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { IdpProvisioningOutcomeDto } from './IdpProvisioningOutcomeDto';
import {
    IdpProvisioningOutcomeDtoFromJSON,
    IdpProvisioningOutcomeDtoFromJSONTyped,
    IdpProvisioningOutcomeDtoToJSON,
    IdpProvisioningOutcomeDtoToJSONTyped,
} from './IdpProvisioningOutcomeDto';
import type { UserTenantDto } from './UserTenantDto';
import {
    UserTenantDtoFromJSON,
    UserTenantDtoFromJSONTyped,
    UserTenantDtoToJSON,
    UserTenantDtoToJSONTyped,
} from './UserTenantDto';
import type { UserPhoneNumberDto } from './UserPhoneNumberDto';
import {
    UserPhoneNumberDtoFromJSON,
    UserPhoneNumberDtoFromJSONTyped,
    UserPhoneNumberDtoToJSON,
    UserPhoneNumberDtoToJSONTyped,
} from './UserPhoneNumberDto';

/**
 * User data transfer object with audit information
 * @export
 * @interface UserDto
 */
export interface UserDto {
    /**
     * 
     * @type {boolean}
     * @memberof UserDto
     */
    _protected?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof UserDto
     */
    active?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof UserDto
     */
    builtInUser?: boolean;
    /**
     * Unique user identifier
     * @type {string}
     * @memberof UserDto
     */
    id?: string;
    /**
     * User's email address (email claim from JWT token)
     * @type {string}
     * @memberof UserDto
     */
    email?: string;
    /**
     * User's full name
     * @type {string}
     * @memberof UserDto
     */
    name?: string;
    /**
     * User's given name
     * @type {string}
     * @memberof UserDto
     */
    givenName?: string;
    /**
     * User's family name
     * @type {string}
     * @memberof UserDto
     */
    familyName?: string;
    /**
     * User's profile picture URL
     * @type {string}
     * @memberof UserDto
     */
    picture?: string;
    /**
     * User's preferred username
     * @type {string}
     * @memberof UserDto
     */
    preferredUsername?: string;
    /**
     * User's phone numbers (supports multiple phone numbers)
     * @type {Array<UserPhoneNumberDto>}
     * @memberof UserDto
     */
    phoneNumbers?: Array<UserPhoneNumberDto>;
    /**
     * Identity provider used for authentication
     * @type {string}
     * @memberof UserDto
     */
    identityProvider?: UserDtoIdentityProviderEnum;
    /**
     * Identity provider configuration value (consistent with SSO config)
     * @type {string}
     * @memberof UserDto
     */
    identityProviderConfig?: UserDtoIdentityProviderConfigEnum;
    /**
     * Provider-specific user ID
     * @type {string}
     * @memberof UserDto
     */
    providerUserId?: string;
    /**
     * Whether the user account is active
     * @type {boolean}
     * @memberof UserDto
     */
    isActive?: boolean;
    /**
     * User account status
     * @type {string}
     * @memberof UserDto
     */
    status?: UserDtoStatusEnum;
    /**
     * When the SSO user was linked to their SSO identity (SSO users only)
     * @type {Date}
     * @memberof UserDto
     */
    ssoLinkedAt?: Date;
    /**
     * User's last login timestamp
     * @type {Date}
     * @memberof UserDto
     */
    lastLogin?: Date;
    /**
     * When the user's password was last changed (local auth only)
     * @type {Date}
     * @memberof UserDto
     */
    lastPasswordChange?: Date;
    /**
     * When the user's profile was last updated
     * @type {Date}
     * @memberof UserDto
     */
    profileUpdatedAt?: Date;
    /**
     * User's preferred timezone
     * @type {string}
     * @memberof UserDto
     */
    preferredTimezone?: string;
    /**
     * When the timezone preference was last updated
     * @type {Date}
     * @memberof UserDto
     */
    timezoneUpdatedAt?: Date;
    /**
     * When this record was created (UTC)
     * @type {Date}
     * @memberof UserDto
     */
    createdAt?: Date;
    /**
     * When this record was last updated (UTC)
     * @type {Date}
     * @memberof UserDto
     */
    updatedAt?: Date;
    /**
     * Whether this is a system-built-in user
     * @type {boolean}
     * @memberof UserDto
     */
    isBuiltInUser?: boolean;
    /**
     * Category of built-in user (if applicable)
     * @type {string}
     * @memberof UserDto
     */
    builtInUserCategory?: UserDtoBuiltInUserCategoryEnum;
    /**
     * Whether this user is protected from deletion
     * @type {boolean}
     * @memberof UserDto
     */
    isProtected?: boolean;
    /**
     * User ID who created this record
     * @type {string}
     * @memberof UserDto
     */
    createdByUserId?: string;
    /**
     * Username who created this record
     * @type {string}
     * @memberof UserDto
     */
    createdByUsername?: string;
    /**
     * User ID who last updated this record
     * @type {string}
     * @memberof UserDto
     */
    updatedByUserId?: string;
    /**
     * Username who last updated this record
     * @type {string}
     * @memberof UserDto
     */
    updatedByUsername?: string;
    /**
     * Current tenant ID context for this user (tenant-scoped responses)
     * @type {string}
     * @memberof UserDto
     */
    tenantId?: string;
    /**
     * Current tenant name (tenant-scoped responses)
     * @type {string}
     * @memberof UserDto
     */
    tenantName?: string;
    /**
     * Current tenant slug (tenant-scoped responses)
     * @type {string}
     * @memberof UserDto
     */
    tenantSlug?: string;
    /**
     * Current customer ID for the tenant (tenant-scoped responses)
     * @type {string}
     * @memberof UserDto
     */
    customerId?: string;
    /**
     * Current customer name for the tenant (tenant-scoped responses)
     * @type {string}
     * @memberof UserDto
     */
    customerName?: string;
    /**
     * Extended metadata/attributes for the user (JSON object for custom fields)
     * @type {{ [key: string]: any; }}
     * @memberof UserDto
     */
    metadata?: { [key: string]: any; };
    /**
     * Most recent IdP claim mapping provisioning outcome for the current tenant context
     * @type {IdpProvisioningOutcomeDto}
     * @memberof UserDto
     */
    idpProvisioningOutcome?: IdpProvisioningOutcomeDto;
    /**
     * User's tenant memberships (filtered by caller's effective tenants)
     * @type {Array<UserTenantDto>}
     * @memberof UserDto
     */
    memberships?: Array<UserTenantDto>;
}


/**
 * @export
 */
export const UserDtoIdentityProviderEnum = {
    Keycloak: 'keycloak',
    Azure: 'azure',
    Okta: 'okta',
    Google: 'google',
    Custom: 'custom'
} as const;
export type UserDtoIdentityProviderEnum = typeof UserDtoIdentityProviderEnum[keyof typeof UserDtoIdentityProviderEnum];

/**
 * @export
 */
export const UserDtoIdentityProviderConfigEnum = {
    Keycloak: 'keycloak',
    Azure: 'azure',
    Okta: 'okta',
    Google: 'google',
    Custom: 'custom'
} as const;
export type UserDtoIdentityProviderConfigEnum = typeof UserDtoIdentityProviderConfigEnum[keyof typeof UserDtoIdentityProviderConfigEnum];

/**
 * @export
 */
export const UserDtoStatusEnum = {
    SsoLinked: 'SSO_LINKED',
    Active: 'ACTIVE'
} as const;
export type UserDtoStatusEnum = typeof UserDtoStatusEnum[keyof typeof UserDtoStatusEnum];

/**
 * @export
 */
export const UserDtoBuiltInUserCategoryEnum = {
    Admin: 'admin',
    Integration: 'integration',
    Monitoring: 'monitoring',
    Custom: 'custom'
} as const;
export type UserDtoBuiltInUserCategoryEnum = typeof UserDtoBuiltInUserCategoryEnum[keyof typeof UserDtoBuiltInUserCategoryEnum];


/**
 * Check if a given object implements the UserDto interface.
 */
export function instanceOfUserDto(value: object): value is UserDto {
    return true;
}

export function UserDtoFromJSON(json: any): UserDto {
    return UserDtoFromJSONTyped(json, false);
}

export function UserDtoFromJSONTyped(json: any, ignoreDiscriminator: boolean): UserDto {
    if (json == null) {
        return json;
    }
    return {
        
        '_protected': json['protected'] == null ? undefined : json['protected'],
        'active': json['active'] == null ? undefined : json['active'],
        'builtInUser': json['builtInUser'] == null ? undefined : json['builtInUser'],
        'id': json['id'] == null ? undefined : json['id'],
        'email': json['email'] == null ? undefined : json['email'],
        'name': json['name'] == null ? undefined : json['name'],
        'givenName': json['givenName'] == null ? undefined : json['givenName'],
        'familyName': json['familyName'] == null ? undefined : json['familyName'],
        'picture': json['picture'] == null ? undefined : json['picture'],
        'preferredUsername': json['preferredUsername'] == null ? undefined : json['preferredUsername'],
        'phoneNumbers': json['phoneNumbers'] == null ? undefined : ((json['phoneNumbers'] as Array<any>).map(UserPhoneNumberDtoFromJSON)),
        'identityProvider': json['identityProvider'] == null ? undefined : json['identityProvider'],
        'identityProviderConfig': json['identityProviderConfig'] == null ? undefined : json['identityProviderConfig'],
        'providerUserId': json['providerUserId'] == null ? undefined : json['providerUserId'],
        'isActive': json['isActive'] == null ? undefined : json['isActive'],
        'status': json['status'] == null ? undefined : json['status'],
        'ssoLinkedAt': json['ssoLinkedAt'] == null ? undefined : (new Date(json['ssoLinkedAt'])),
        'lastLogin': json['lastLogin'] == null ? undefined : (new Date(json['lastLogin'])),
        'lastPasswordChange': json['lastPasswordChange'] == null ? undefined : (new Date(json['lastPasswordChange'])),
        'profileUpdatedAt': json['profileUpdatedAt'] == null ? undefined : (new Date(json['profileUpdatedAt'])),
        'preferredTimezone': json['preferredTimezone'] == null ? undefined : json['preferredTimezone'],
        'timezoneUpdatedAt': json['timezoneUpdatedAt'] == null ? undefined : (new Date(json['timezoneUpdatedAt'])),
        'createdAt': json['createdAt'] == null ? undefined : (new Date(json['createdAt'])),
        'updatedAt': json['updatedAt'] == null ? undefined : (new Date(json['updatedAt'])),
        'isBuiltInUser': json['isBuiltInUser'] == null ? undefined : json['isBuiltInUser'],
        'builtInUserCategory': json['builtInUserCategory'] == null ? undefined : json['builtInUserCategory'],
        'isProtected': json['isProtected'] == null ? undefined : json['isProtected'],
        'createdByUserId': json['createdByUserId'] == null ? undefined : json['createdByUserId'],
        'createdByUsername': json['createdByUsername'] == null ? undefined : json['createdByUsername'],
        'updatedByUserId': json['updatedByUserId'] == null ? undefined : json['updatedByUserId'],
        'updatedByUsername': json['updatedByUsername'] == null ? undefined : json['updatedByUsername'],
        'tenantId': json['tenantId'] == null ? undefined : json['tenantId'],
        'tenantName': json['tenantName'] == null ? undefined : json['tenantName'],
        'tenantSlug': json['tenantSlug'] == null ? undefined : json['tenantSlug'],
        'customerId': json['customerId'] == null ? undefined : json['customerId'],
        'customerName': json['customerName'] == null ? undefined : json['customerName'],
        'metadata': json['metadata'] == null ? undefined : json['metadata'],
        'idpProvisioningOutcome': json['idpProvisioningOutcome'] == null ? undefined : IdpProvisioningOutcomeDtoFromJSON(json['idpProvisioningOutcome']),
        'memberships': json['memberships'] == null ? undefined : ((json['memberships'] as Array<any>).map(UserTenantDtoFromJSON)),
    };
}

export function UserDtoToJSON(json: any): UserDto {
    return UserDtoToJSONTyped(json, false);
}

export function UserDtoToJSONTyped(value?: UserDto | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'protected': value['_protected'],
        'active': value['active'],
        'builtInUser': value['builtInUser'],
        'id': value['id'],
        'email': value['email'],
        'name': value['name'],
        'givenName': value['givenName'],
        'familyName': value['familyName'],
        'picture': value['picture'],
        'preferredUsername': value['preferredUsername'],
        'phoneNumbers': value['phoneNumbers'] == null ? undefined : ((value['phoneNumbers'] as Array<any>).map(UserPhoneNumberDtoToJSON)),
        'identityProvider': value['identityProvider'],
        'identityProviderConfig': value['identityProviderConfig'],
        'providerUserId': value['providerUserId'],
        'isActive': value['isActive'],
        'status': value['status'],
        'ssoLinkedAt': value['ssoLinkedAt'] == null ? undefined : ((value['ssoLinkedAt']).toISOString()),
        'lastLogin': value['lastLogin'] == null ? undefined : ((value['lastLogin']).toISOString()),
        'lastPasswordChange': value['lastPasswordChange'] == null ? undefined : ((value['lastPasswordChange']).toISOString()),
        'profileUpdatedAt': value['profileUpdatedAt'] == null ? undefined : ((value['profileUpdatedAt']).toISOString()),
        'preferredTimezone': value['preferredTimezone'],
        'timezoneUpdatedAt': value['timezoneUpdatedAt'] == null ? undefined : ((value['timezoneUpdatedAt']).toISOString()),
        'createdAt': value['createdAt'] == null ? undefined : ((value['createdAt']).toISOString()),
        'updatedAt': value['updatedAt'] == null ? undefined : ((value['updatedAt']).toISOString()),
        'isBuiltInUser': value['isBuiltInUser'],
        'builtInUserCategory': value['builtInUserCategory'],
        'isProtected': value['isProtected'],
        'createdByUserId': value['createdByUserId'],
        'createdByUsername': value['createdByUsername'],
        'updatedByUserId': value['updatedByUserId'],
        'updatedByUsername': value['updatedByUsername'],
        'tenantId': value['tenantId'],
        'tenantName': value['tenantName'],
        'tenantSlug': value['tenantSlug'],
        'customerId': value['customerId'],
        'customerName': value['customerName'],
        'metadata': value['metadata'],
        'idpProvisioningOutcome': IdpProvisioningOutcomeDtoToJSON(value['idpProvisioningOutcome']),
        'memberships': value['memberships'] == null ? undefined : ((value['memberships'] as Array<any>).map(UserTenantDtoToJSON)),
    };
}

